import useAuth from '@app/auth/use-auth';
import { useAccount } from '@shared/api';
import { FoodActivitiesList } from '@shared/api/types';
import Text from '@shared/components/content/text';
import Unit from '@shared/components/content/unit';
import { ActivityCell } from '@shared/components/data-table/cells/activity-cell';
import { HeaderCell } from '@shared/components/data-table/cells/header-cell';
import { NumberCell } from '@shared/components/data-table/cells/number-cell';
import { TextCell } from '@shared/components/data-table/cells/text-cell';
import RatingCloud, {
  RatingCloudProps,
} from '@shared/components/ratings/rating-cloud';
import { formatCost, getCurrencySymbol } from '@shared/lib/format';
import { formatQuantity, formatSmallNumber } from '@shared/lib/utils';
import { ColumnDef } from '@tanstack/react-table';

export function useFoodTableColumns(): ColumnDef<FoodActivitiesList>[] {
  const { user } = useAuth();
  const hasCostFeature = !!user?.features.cost;
  const { data: account } = useAccount(true);
  const currency = account?.defaultCurrency;

  const columns: ColumnDef<FoodActivitiesList>[] = [
    {
      accessorKey: 'uuid',
      enableHiding: true,
    },
    {
      accessorKey: 'period',
      enableHiding: true,
    },
    {
      accessorKey: 'site',
      enableHiding: true,
    },
    {
      id: 'food',
      accessorKey: 'food',
      accessorFn: (row) => row.purchasedGoodInfo,
      header: ({ column }) => <HeaderCell column={column} name="Name" />,
      cell: ({ getValue, row }) => {
        const remoteId = row.original.remoteId;
        const food = getValue<FoodActivitiesList['purchasedGoodInfo']>();
        return (
          <ActivityCell
            className="w-full min-w-40"
            name={food.name}
            remoteId={remoteId!}
          />
        );
      },
    },
    {
      accessorKey: 'matchedFood',
      accessorFn: (row) => row.purchasedGoodInfo,
      header: ({ column }) => (
        <HeaderCell column={column} name="Matched food" />
      ),
      cell: ({ getValue }) => {
        const purchasedGood =
          getValue<FoodActivitiesList['purchasedGoodInfo']>();
        return <Text>{purchasedGood.basicFoodName || purchasedGood.name}</Text>;
      },
    },
    {
      accessorKey: 'matchedRating',
      accessorFn: (row) => row.emissionsCategory,
      header: ({ column }) => <HeaderCell column={column} name="Rating" />,
      cell: ({ getValue }) => {
        const category = getValue<FoodActivitiesList['emissionsCategory']>();
        return <RatingCloud rating={category as RatingCloudProps['rating']} />;
      },
      enableSorting: false,
    },
    {
      accessorKey: 'emissionFactor',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Emission Factor"
          numberColumn
          unit={<Unit variant="kgCO2e/kg" />}
        />
      ),
      cell: ({ getValue }) => {
        const emissionFactor = getValue<number>();
        return <NumberCell number={emissionFactor} />;
      },
    },
    {
      accessorKey: 'quantity',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Quantity"
          numberColumn
          unit={<Unit variant="kg" />}
        />
      ),
      cell: ({ getValue }) => {
        const quantity = getValue<number>();
        return <NumberCell number={quantity} formatter={formatQuantity} />;
      },
    },
    {
      accessorKey: 'emissions',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Emissions"
          numberColumn
          unit={<Unit variant="tCO2e" />}
        />
      ),
      cell: ({ getValue }) => {
        return (
          <TextCell
            text={formatSmallNumber(getValue<number>())}
            type="number"
          />
        );
      },
    },
  ];

  if (hasCostFeature) {
    columns.push(
      {
        accessorKey: 'totalSpend',
        header: ({ column }) => (
          <HeaderCell column={column} name="Total spend" numberColumn />
        ),
        cell: ({ getValue }) => {
          const totalSpend = getValue<number | null | undefined>();
          return (
            <TextCell text={formatCost(totalSpend, currency)} type="number" />
          );
        },
      },
      {
        accessorKey: 'costPerKg',
        header: ({ column }) => (
          <HeaderCell
            column={column}
            name="Average price"
            numberColumn
            unit={<Unit variant={`${getCurrencySymbol(currency)}/kg`} />}
          />
        ),
        cell: ({ getValue }) => {
          const costPerKg = getValue<number | null | undefined>();
          return (
            <TextCell text={formatCost(costPerKg, currency)} type="number" />
          );
        },
      }
    );
  }

  return columns;
}
